import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchData } from 'services/api/useFetchData';
import '../../../style/scss/email-preview.scss';

interface LayoutExampleProps {
    layoutId?: number;
    reloadLayout?: number | null;
    emailData?: {
        send_to?: string[];
        specific_email?: string;
        bcc_recipient?: string;
        from_email?: string;
        reply_to?: string;
        subject?: string,
        body?: string,
    }
}

const EmailLayoutExample: React.FC<LayoutExampleProps> = ({ 
    layoutId, reloadLayout, emailData
}) => {
    const { t } = useTranslation();
    const shadowRootRef = useRef<HTMLDivElement>(null);
    const [htmlContent, setHtmlContent] = useState<string | null>(null);
    const [requestParams, setRequestParams] = useState(() => ({
        ...(layoutId !== null && { layout_id: String(layoutId) }),
        subject: emailData?.subject ?? undefined,
        body: emailData?.body ?? undefined,
    }));
    const prevParamsRef = useRef<string | null>(null);
    
    // Fetch the data
    const { response, refetch } = useFetchData({ 
        apiUrl: 'generate-email-layout-html',
        params: requestParams
    });

    // Set reload counter ref
    const prevReloadCounter = useRef(reloadLayout);

    // Refetch the layout html example when the reload counter is updated
    useEffect(() => {
        if (prevReloadCounter.current !== reloadLayout) {
            refetch();
            prevReloadCounter.current = reloadLayout;
        }
    }, [reloadLayout]);

    // When the html example is fetched, put it inside the html content
    useEffect(() => {
        if (response) {
            setHtmlContent(response.html);
        }
    }, [response]);

    // Create the shadow root
    useEffect(() => {
        if (shadowRootRef.current && htmlContent) {
            // Only create a new shadow root if it not yet exist
            if (!shadowRootRef.current.shadowRoot) {
                const shadowRoot = shadowRootRef.current.attachShadow({ mode: 'open' });
                shadowRoot.innerHTML = htmlContent;
            } else {
                // If a shadow root already exists, just update it
                shadowRootRef.current.shadowRoot.innerHTML = htmlContent;
            }
        }
    }, [htmlContent]);

    // Refetch the html example when the layout or email data is updated
    useEffect(() => {

        // Update the params
        const params = {
            ...(layoutId != null && { layout_id: String(layoutId) }),
            subject: emailData?.subject ?? "",
            body: emailData?.body ?? "",
        };

        // Generate the params string
        const paramsString = JSON.stringify(params);

        // Refetch if the params string is changed
        const timer = setTimeout(() => {
            if (paramsString !== prevParamsRef.current) {
                prevParamsRef.current = paramsString;

                setRequestParams(params);
                refetch();
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [layoutId, emailData?.subject, emailData?.body]);

    // Generate the send to value
    const generateSendToValue = (send_to: string[], specific_email: string | undefined): string => {

        // Map send to items to their translations, excluding specific email
        const translatedSendTo = send_to
            .filter(type => type !== 'specific_email')
            .map(type => t('email.email_template.send_to_uncapitalized', { 
                send_to: t(`email.email_template.send_to.${type}`)
            }));

        // Combine translated items with specific email if present
        const generatedSendTo = translatedSendTo.length > 0 
            ? specific_email 
                ? `${translatedSendTo.join(", ")}, ${specific_email}` 
                : translatedSendTo.join(", ")
            : specific_email || ''

        return generatedSendTo;
    }

    // Configure the email header fields
    const emailHeaderFields: { label: string; value?: string | string[] }[] = [
        { label: t('email.general.to_short_label'), value: emailData?.send_to?.length ? generateSendToValue(emailData?.send_to, emailData?.specific_email) : undefined},
        { label: t('email.general.bcc_short_label'), value: emailData?.bcc_recipient },
        { label: t('email.general.from_short_label'), value: emailData?.from_email },
        { label: t('email.general.reply_to_label'), value: emailData?.reply_to },
        { label: t('email.general.subject_label'), value: emailData?.subject },
    ];

    return (
        <div className='email-layout-example'>
            <div className='header'>
                <h6>
                    {t('email.email_layout.email_example_title')}
                </h6>
                {emailHeaderFields.some(field => field.value) && (
                    <div className='email-header-fields'>
                        {emailHeaderFields.map((field, index) => field.value && (
                            <p key={index}>
                                {field.label}: {field.value}
                            </p>
                        ))}
                    </div>
                )}
            </div>
            <div className='preview' ref={shadowRootRef} />
        </div>
    );
};

export default EmailLayoutExample;